import { request } from '../base';
import type { ApiResponse, PaginatedResponse, PaginationParams } from '../base';

// =====================================================
// PHASE 36: WORKFLOW AUTOMATION & APPROVALS
// =====================================================

// Workflow Definitions
export async function getWorkflowDefinitions(params?: { category?: string; workflowType?: string; isActive?: boolean; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  if (params?.workflowType) searchParams.set('workflowType', params.workflowType);
  if (params?.isActive !== undefined) searchParams.set('isActive', String(params.isActive));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/workflows/definitions${query ? `?${query}` : ''}`);
}

export async function getWorkflowDefinition(id: string) {
  return request<any>(`/workflows/definitions/${id}`);
}

export async function createWorkflowDefinition(data: {
  code: string;
  name: string;
  description?: string;
  workflowType: string;
  category: string;
  triggerType: string;
  triggerConfig?: any;
  isActive?: boolean;
  priority?: number;
  slaHours?: number;
  escalationHours?: number;
  allowParallel?: boolean;
  requireAllApprovals?: boolean;
  autoApproveConditions?: any;
  metadata?: any;
  steps?: Array<{
    stepNumber: number;
    stepName: string;
    stepType: string;
    description?: string;
    assigneeType: string;
    assigneeId?: string;
    assigneeRule?: any;
    actionConfig?: any;
    conditionConfig?: any;
    notificationConfig?: any;
    formConfig?: any;
    requiredFields?: string[];
    slaHours?: number;
    escalationConfig?: any;
    onApproveGoto?: string;
    onRejectGoto?: string;
    onTimeoutAction?: string;
    allowDelegation?: boolean;
    allowComments?: boolean;
    requireSignature?: boolean;
    isOptional?: boolean;
    metadata?: any;
  }>;
}) {
  return request<any>('/workflows/definitions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWorkflowDefinition(id: string, data: Partial<{
  code: string;
  name: string;
  description: string;
  workflowType: string;
  category: string;
  triggerType: string;
  triggerConfig: any;
  isActive: boolean;
  priority: number;
  slaHours: number;
  escalationHours: number;
  allowParallel: boolean;
  requireAllApprovals: boolean;
  autoApproveConditions: any;
  metadata: any;
}>) {
  return request<any>(`/workflows/definitions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteWorkflowDefinition(id: string) {
  return request<{ success: boolean }>(`/workflows/definitions/${id}`, { method: 'DELETE' });
}

// Workflow Steps
export async function getWorkflowSteps(workflowId: string) {
  return request<any[]>(`/workflows/definitions/${workflowId}/steps`);
}

export async function createWorkflowStep(workflowId: string, data: {
  stepNumber: number;
  stepName: string;
  stepType: string;
  description?: string;
  assigneeType: string;
  assigneeId?: string;
  assigneeRule?: any;
  actionConfig?: any;
  conditionConfig?: any;
  notificationConfig?: any;
  formConfig?: any;
  requiredFields?: string[];
  slaHours?: number;
  escalationConfig?: any;
  onApproveGoto?: string;
  onRejectGoto?: string;
  onTimeoutAction?: string;
  allowDelegation?: boolean;
  allowComments?: boolean;
  requireSignature?: boolean;
  isOptional?: boolean;
  metadata?: any;
}) {
  return request<any>(`/workflows/definitions/${workflowId}/steps`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWorkflowStep(id: string, data: Partial<{
  stepNumber: number;
  stepName: string;
  stepType: string;
  description: string;
  assigneeType: string;
  assigneeId: string;
  assigneeRule: any;
  actionConfig: any;
  conditionConfig: any;
  notificationConfig: any;
  formConfig: any;
  requiredFields: string[];
  slaHours: number;
  escalationConfig: any;
  onApproveGoto: string;
  onRejectGoto: string;
  onTimeoutAction: string;
  allowDelegation: boolean;
  allowComments: boolean;
  requireSignature: boolean;
  isOptional: boolean;
  metadata: any;
}>) {
  return request<any>(`/workflows/steps/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteWorkflowStep(id: string) {
  return request<{ success: boolean }>(`/workflows/steps/${id}`, { method: 'DELETE' });
}

// Workflow Instances
export async function getWorkflowInstances(params?: { status?: string; workflowId?: string; entityType?: string; entityId?: string; assignedToMe?: boolean; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.workflowId) searchParams.set('workflowId', params.workflowId);
  if (params?.entityType) searchParams.set('entityType', params.entityType);
  if (params?.entityId) searchParams.set('entityId', params.entityId);
  if (params?.assignedToMe) searchParams.set('assignedToMe', 'true');
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/workflows/instances${query ? `?${query}` : ''}`);
}

export async function getWorkflowInstance(id: string) {
  return request<any>(`/workflows/instances/${id}`);
}

export async function startWorkflow(data: {
  workflowId: string;
  entityType: string;
  entityId: string;
  entityName?: string;
  priority?: number;
  contextData?: any;
  metadata?: any;
}) {
  return request<any>('/workflows/instances', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function takeWorkflowAction(stepInstanceId: string, data: {
  action: 'approve' | 'reject' | 'request_info';
  decision?: string;
  comments?: string;
  formData?: any;
}) {
  return request<{ success: boolean; newStatus: string }>(`/workflows/step-instances/${stepInstanceId}/action`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function delegateWorkflowStep(stepInstanceId: string, data: {
  delegateTo: string;
  reason?: string;
}) {
  return request<{ success: boolean }>(`/workflows/step-instances/${stepInstanceId}/delegate`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function cancelWorkflow(instanceId: string, reason?: string) {
  return request<{ success: boolean }>(`/workflows/instances/${instanceId}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// Approval Matrices
export async function getApprovalMatrices(params?: { category?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/workflows/matrices${query ? `?${query}` : ''}`);
}

export async function getApprovalMatrix(id: string) {
  return request<any>(`/workflows/matrices/${id}`);
}

export async function createApprovalMatrix(data: {
  name: string;
  description?: string;
  category: string;
  isActive?: boolean;
  priority?: number;
  metadata?: any;
  rules?: Array<{
    ruleName: string;
    ruleOrder: number;
    conditionType: string;
    conditionField?: string;
    conditionOperator?: string;
    conditionValue?: string;
    conditionValue2?: string;
    approverType: string;
    approverId?: string;
    approverLevel?: number;
    isRequired?: boolean;
    slaHours?: number;
    isActive?: boolean;
    metadata?: any;
  }>;
}) {
  return request<any>('/workflows/matrices', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateApprovalMatrix(id: string, data: Partial<{
  name: string;
  description: string;
  category: string;
  isActive: boolean;
  priority: number;
  metadata: any;
}>) {
  return request<any>(`/workflows/matrices/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteApprovalMatrix(id: string) {
  return request<{ success: boolean }>(`/workflows/matrices/${id}`, { method: 'DELETE' });
}

// Delegation Rules
export async function getDelegationRules(myDelegations?: boolean) {
  const searchParams = new URLSearchParams();
  if (myDelegations) searchParams.set('myDelegations', 'true');
  const query = searchParams.toString();
  return request<any[]>(`/workflows/delegations${query ? `?${query}` : ''}`);
}

export async function createDelegationRule(data: {
  delegatorId?: string;
  delegateId: string;
  delegationType: string;
  category?: string;
  workflowId?: string;
  startDate: string;
  endDate?: string;
  reason?: string;
  isActive?: boolean;
  notifyDelegator?: boolean;
}) {
  return request<any>('/workflows/delegations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDelegationRule(id: string, data: Partial<{
  delegateId: string;
  delegationType: string;
  category: string;
  workflowId: string;
  startDate: string;
  endDate: string;
  reason: string;
  isActive: boolean;
  notifyDelegator: boolean;
}>) {
  return request<any>(`/workflows/delegations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteDelegationRule(id: string) {
  return request<{ success: boolean }>(`/workflows/delegations/${id}`, { method: 'DELETE' });
}

// Workflow Templates
export async function getWorkflowTemplates(params?: { category?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/workflows/templates${query ? `?${query}` : ''}`);
}

export async function getWorkflowTemplate(id: string) {
  return request<any>(`/workflows/templates/${id}`);
}

export async function createWorkflowTemplate(data: {
  name: string;
  description?: string;
  category: string;
  templateData: any;
  isPublic?: boolean;
  isSystem?: boolean;
  version?: string;
  tags?: string[];
}) {
  return request<any>('/workflows/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createWorkflowFromTemplate(templateId: string, data?: { code?: string; name?: string; description?: string }) {
  return request<any>(`/workflows/templates/${templateId}/create-workflow`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

// Workflow Comments
export async function getWorkflowComments(instanceId: string) {
  return request<any[]>(`/workflows/instances/${instanceId}/comments`);
}

export async function addWorkflowComment(instanceId: string, data: {
  stepInstanceId?: string;
  commentType?: string;
  content: string;
  attachments?: any;
  isInternal?: boolean;
  parentCommentId?: string;
}) {
  return request<any>(`/workflows/instances/${instanceId}/comments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Workflow Audit Trail
export async function getWorkflowAuditTrail(instanceId: string) {
  return request<any[]>(`/workflows/instances/${instanceId}/audit-trail`);
}

// Workflow Metrics
export async function getWorkflowMetrics(params?: { workflowId?: string; periodType?: string; startDate?: string; endDate?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.workflowId) searchParams.set('workflowId', params.workflowId);
  if (params?.periodType) searchParams.set('periodType', params.periodType);
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  const query = searchParams.toString();
  return request<any[]>(`/workflows/metrics${query ? `?${query}` : ''}`);
}

// My Tasks (Pending Approvals)
export async function getMyWorkflowTasks(params?: { status?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/workflows/my-tasks${query ? `?${query}` : ''}`);
}

// Workflow Favorites
export async function getWorkflowFavorites() {
  return request<any[]>('/workflows/favorites');
}

export async function addWorkflowFavorite(data: {
  workflowId?: string;
  instanceId?: string;
  favoriteType: string;
  notes?: string;
}) {
  return request<any>('/workflows/favorites', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function removeWorkflowFavorite(id: string) {
  return request<{ success: boolean }>(`/workflows/favorites/${id}`, { method: 'DELETE' });
}

// Workflow Dashboard
export async function getWorkflowDashboard() {
  return request<{
    activeWorkflows: number;
    pendingInstances: number;
    myPendingTasks: number;
    overdueTasks: number;
    recentActivity: any[];
  }>('/workflows/dashboard');
}

// ================================
// Compliance
// ================================

export async function getComplianceRequirements(params?: { category?: string; isActive?: boolean; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.category) query.set('category', params.category);
  if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/compliance/requirements${queryString ? `?${queryString}` : ''}`);
}

export async function getComplianceRequirement(id: string) {
  return request<any>(`/compliance/requirements/${id}`);
}

export async function createComplianceRequirement(data: {
  code: string;
  name: string;
  description?: string;
  category: string;
  regulatoryBody?: string;
  applicableRegions?: string[];
  requirements?: any;
  evidenceTypes?: string[];
  reviewFrequency?: string;
  isActive?: boolean;
  effectiveDate?: string;
  expiryDate?: string;
}) {
  return request<any>('/compliance/requirements', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateComplianceRequirement(id: string, data: Record<string, any>) {
  return request<any>(`/compliance/requirements/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getComplianceAssessments(params?: { requirementId?: string; status?: string; complianceLevel?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.requirementId) query.set('requirementId', params.requirementId);
  if (params?.status) query.set('status', params.status);
  if (params?.complianceLevel) query.set('complianceLevel', params.complianceLevel);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/compliance/assessments${queryString ? `?${queryString}` : ''}`);
}

export async function getComplianceAssessment(id: string) {
  return request<any>(`/compliance/assessments/${id}`);
}

export async function createComplianceAssessment(data: {
  requirementId: string;
  assessmentDate?: string;
  status?: string;
  complianceLevel?: string;
  findings?: string;
  evidence?: any;
  correctiveActions?: any;
  riskRating?: string;
  dueDate?: string;
  notes?: string;
}) {
  return request<any>('/compliance/assessments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function completeComplianceAssessment(id: string, data: { complianceLevel: string; findings?: string }) {
  return request<any>(`/compliance/assessments/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getRetentionPolicies() {
  return request<{ data: any[] }>('/compliance/retention-policies');
}

export async function createRetentionPolicy(data: {
  name: string;
  description?: string;
  entityType: string;
  retentionPeriodDays: number;
  archiveBeforeDelete?: boolean;
  isActive?: boolean;
  conditions?: any;
}) {
  return request<any>('/compliance/retention-policies', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function executeRetentionPolicy(id: string) {
  return request<{ executionId: string; recordsReviewed: number; recordsArchived: number; recordsDeleted: number }>(`/compliance/retention-policies/${id}/execute`, {
    method: 'POST',
  });
}

export async function getGdprRequests(params?: { requestType?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.requestType) query.set('requestType', params.requestType);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/compliance/gdpr-requests${queryString ? `?${queryString}` : ''}`);
}

export async function createGdprRequest(data: {
  requestType: string;
  requestorEmail: string;
  requestorName: string;
  subjectUserId?: string;
  priority?: string;
  dataCategories?: string[];
  requestDetails?: string;
  assignedTo?: string;
}) {
  return request<any>('/compliance/gdpr-requests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function completeGdprRequest(id: string, data: { responseDetails?: string; dataExportPath?: string }) {
  return request<any>(`/compliance/gdpr-requests/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getComplianceReports(params?: { reportType?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.reportType) query.set('reportType', params.reportType);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/compliance/reports${queryString ? `?${queryString}` : ''}`);
}

export async function generateComplianceReport(data: {
  reportType: string;
  title: string;
  periodStart: string;
  periodEnd: string;
  summary?: string;
  findings?: any;
  recommendations?: any;
}) {
  return request<any>('/compliance/reports/generate', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function approveComplianceReport(id: string) {
  return request<any>(`/compliance/reports/${id}/approve`, {
    method: 'POST',
  });
}

export async function getDataClassifications() {
  return request<{ data: any[] }>('/compliance/data-classifications');
}

export async function createDataClassification(data: {
  name: string;
  description?: string;
  sensitivityLevel: number;
  handlingRequirements?: string;
  retentionRequirements?: string;
  encryptionRequired?: boolean;
  accessRestrictions?: any;
  isActive?: boolean;
}) {
  return request<any>('/compliance/data-classifications', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getComplianceDashboard() {
  return request<{
    requirements: { total: number; active: number };
    assessments: { total: number; compliant: number; partiallyCompliant: number; nonCompliant: number; pending: number };
    gdpr: { total: number; pending: number; inProgress: number; overdue: number };
    upcomingAssessments: any[];
  }>('/compliance/dashboard');
}

// ================================
// Integrations
// ================================

export async function getIntegrations(params?: { type?: string; isActive?: boolean; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.type) query.set('type', params.type);
  if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/integrations${queryString ? `?${queryString}` : ''}`);
}

export async function getIntegration(id: string) {
  return request<any>(`/integrations/${id}`);
}

export async function createIntegration(data: {
  name: string;
  type: string;
  description?: string;
  config?: Record<string, any>;
  credentials?: Record<string, any>;
  isActive?: boolean;
  syncDirection?: string;
  syncIntervalMinutes?: number;
  fieldMappings?: any;
}) {
  return request<any>('/integrations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateIntegration(id: string, data: Record<string, any>) {
  return request<any>(`/integrations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteIntegration(id: string) {
  return request<{ success: boolean }>(`/integrations/${id}`, {
    method: 'DELETE',
  });
}

export async function testIntegrationConnection(id: string) {
  return request<{ success: boolean; message: string; details?: any }>(`/integrations/${id}/test`, {
    method: 'POST',
  });
}

export async function triggerIntegrationSync(id: string, data?: { syncType?: string; direction?: string }) {
  return request<{
    syncId: string;
    status: string;
    recordsProcessed: number;
    recordsCreated: number;
    recordsUpdated: number;
    durationMs: number;
  }>(`/integrations/${id}/sync`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

export async function getIntegrationSyncLogs(integrationId: string, params?: { status?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/integrations/${integrationId}/sync-logs${queryString ? `?${queryString}` : ''}`);
}

export async function getEdiDocuments(params?: { documentType?: string; direction?: string; status?: string; tradingPartnerId?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.documentType) query.set('documentType', params.documentType);
  if (params?.direction) query.set('direction', params.direction);
  if (params?.status) query.set('status', params.status);
  if (params?.tradingPartnerId) query.set('tradingPartnerId', params.tradingPartnerId);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/integrations/edi/documents${queryString ? `?${queryString}` : ''}`);
}

export async function getEdiDocument(id: string) {
  return request<any>(`/integrations/edi/documents/${id}`);
}

export async function acknowledgeEdiDocument(id: string) {
  return request<any>(`/integrations/edi/documents/${id}/acknowledge`, {
    method: 'POST',
  });
}

export async function processEdiDocument(id: string, data: { entityType: string; entityId: string }) {
  return request<any>(`/integrations/edi/documents/${id}/process`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createEdiDocument(data: {
  integrationId: string;
  documentType: string;
  tradingPartnerId: string;
  controlNumber?: string;
  rawContent?: string;
  parsedContent?: any;
  relatedEntityType?: string;
  relatedEntityId?: string;
}) {
  return request<any>('/integrations/edi/documents', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getIntegrationEvents(params?: { eventType?: string; entityType?: string; entityId?: string; userId?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.eventType) query.set('eventType', params.eventType);
  if (params?.entityType) query.set('entityType', params.entityType);
  if (params?.entityId) query.set('entityId', params.entityId);
  if (params?.userId) query.set('userId', params.userId);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/integrations/events${queryString ? `?${queryString}` : ''}`);
}

export async function getAvailableIntegrationTypes() {
  return request<{ data: any[] }>('/integrations/types/available');
}

export async function getIntegrationDashboard() {
  return request<{
    integrations: { total: number; active: number; byType: Record<string, number> };
    syncs: { totalSyncs: number; successfulSyncs: number; failedSyncs: number; totalRecordsProcessed: number };
    edi: { totalDocuments: number; inbound: number; outbound: number; pending: number; errors: number };
    recentFailures: any[];
  }>('/integrations/dashboard/stats');
}

// ================================
// Vendor Qualification
// ================================

export async function getVendorQualifications(params?: { status?: string; level?: string; riskLevel?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.level) query.set('level', params.level);
  if (params?.riskLevel) query.set('riskLevel', params.riskLevel);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/vendor-qualification/qualifications${queryString ? `?${queryString}` : ''}`);
}

export async function getVendorQualification(id: string) {
  return request<any>(`/vendor-qualification/qualifications/${id}`);
}

export async function createVendorQualification(data: Record<string, any>) {
  return request<any>('/vendor-qualification/qualifications', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateVendorQualification(id: string, data: Record<string, any>) {
  return request<any>(`/vendor-qualification/qualifications/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getVendorAudits(params?: { search?: string; supplierId?: string; type?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.type) query.set('type', params.type);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/vendor-qualification/audits${queryString ? `?${queryString}` : ''}`);
}

export async function getVendorAudit(id: string) {
  return request<any>(`/vendor-qualification/audits/${id}`);
}

export async function createVendorAudit(data: Record<string, any>) {
  return request<any>('/vendor-qualification/audits', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getVendorScorecards(params?: { supplierId?: string; periodStart?: string; periodEnd?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.periodStart) query.set('periodStart', params.periodStart);
  if (params?.periodEnd) query.set('periodEnd', params.periodEnd);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/vendor-qualification/scorecards${queryString ? `?${queryString}` : ''}`);
}

export async function getVendorScorecard(id: string) {
  return request<any>(`/vendor-qualification/scorecards/${id}`);
}

export async function createVendorScorecard(data: Record<string, any>) {
  return request<any>('/vendor-qualification/scorecards', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getApprovedVendorList(params?: { materialId?: string; supplierId?: string; status?: string; primaryOnly?: boolean; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.materialId) query.set('materialId', params.materialId);
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.status) query.set('status', params.status);
  if (params?.primaryOnly) query.set('primaryOnly', 'true');
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/vendor-qualification/avl${queryString ? `?${queryString}` : ''}`);
}

export async function getVendorQualificationDashboard() {
  return request<{
    qualificationStats: any[];
    riskStats: any[];
    auditStats: any[];
    issueStats: any[];
    expiringDocuments: number;
    pendingQuestionnaires: number;
    avlStats: any[];
    pendingFollowUps: number;
  }>('/vendor-qualification/dashboard');
}

// =====================================================
// PHASE 37: CUSTOMER PORTAL (Additional)
// =====================================================

export async function deactivatePortalUser(id: string) {
  return request<{ success: boolean }>(`/customer-portal/users/${id}/deactivate`, { method: 'POST' });
}

export async function resendPortalUserInvitation(id: string) {
  return request<{ success: boolean }>(`/customer-portal/users/${id}/resend-invitation`, { method: 'POST' });
}

// Portal Announcements
export async function getPortalAnnouncements(params?: { isPublished?: boolean; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.isPublished !== undefined) searchParams.set('isPublished', String(params.isPublished));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/customer-portal/announcements${query ? `?${query}` : ''}`);
}

export async function getPortalAnnouncement(id: string) {
  return request<any>(`/customer-portal/announcements/${id}`);
}

export async function createPortalAnnouncement(data: {
  title: string;
  content: string;
  announcementType?: string;
  priority?: number;
  isPinned?: boolean;
  targetAudience?: string;
  targetCustomerIds?: string[];
  targetUserIds?: string[];
  publishAt?: string;
  expiresAt?: string;
  isPublished?: boolean;
}) {
  return request<any>('/customer-portal/announcements', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePortalAnnouncement(id: string, data: Partial<{
  title: string;
  content: string;
  announcementType: string;
  priority: number;
  isPinned: boolean;
  targetAudience: string;
  targetCustomerIds: string[];
  targetUserIds: string[];
  publishAt: string;
  expiresAt: string;
  isPublished: boolean;
}>) {
  return request<any>(`/customer-portal/announcements/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deletePortalAnnouncement(id: string) {
  return request<{ success: boolean }>(`/customer-portal/announcements/${id}`, { method: 'DELETE' });
}

// Portal Sample Requests
export async function getPortalSampleRequests(params?: { customerId?: string; status?: string; assignedTo?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.customerId) searchParams.set('customerId', params.customerId);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.assignedTo) searchParams.set('assignedTo', params.assignedTo);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/customer-portal/sample-requests${query ? `?${query}` : ''}`);
}

export async function getPortalSampleRequest(id: string) {
  return request<any>(`/customer-portal/sample-requests/${id}`);
}

export async function updatePortalSampleRequest(id: string, data: Partial<{
  status: string;
  priority: string;
  assignedTo: string;
  internalNotes: string;
  rejectionReason: string;
  sampleId: string;
  shippingAddress: any;
  trackingNumber: string;
  shippedAt: string;
  deliveredAt: string;
}>) {
  return request<any>(`/customer-portal/sample-requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function addPortalSampleRequestComment(requestId: string, data: {
  commentType?: string;
  content: string;
  attachments?: any;
  isInternal?: boolean;
}) {
  return request<any>(`/customer-portal/sample-requests/${requestId}/comments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Customer Portal Documents
export async function getCustomerPortalDocuments(params?: { customerId?: string; documentType?: string; category?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.customerId) searchParams.set('customerId', params.customerId);
  if (params?.documentType) searchParams.set('documentType', params.documentType);
  if (params?.category) searchParams.set('category', params.category);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/customer-portal/documents${query ? `?${query}` : ''}`);
}

export async function getCustomerPortalDocument(id: string) {
  return request<any>(`/customer-portal/documents/${id}`);
}

export async function createCustomerPortalDocument(data: {
  customerId?: string;
  documentType: string;
  documentCategory?: string;
  name: string;
  description?: string;
  fileUrl: string;
  fileName: string;
  fileSize?: number;
  fileType?: string;
  version?: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
  isPublic?: boolean;
  isDownloadable?: boolean;
  requiresNda?: boolean;
  expirationDate?: string;
  tags?: string[];
}) {
  return request<any>('/customer-portal/documents', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCustomerPortalDocument(id: string, data: Partial<{
  customerId: string;
  documentType: string;
  documentCategory: string;
  name: string;
  description: string;
  fileUrl: string;
  fileName: string;
  version: string;
  isPublic: boolean;
  isDownloadable: boolean;
  requiresNda: boolean;
  expirationDate: string;
  tags: string[];
}>) {
  return request<any>(`/customer-portal/documents/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCustomerPortalDocument(id: string) {
  return request<{ success: boolean }>(`/customer-portal/documents/${id}`, { method: 'DELETE' });
}

// Portal Quote Requests
export async function getPortalQuoteRequests(params?: { customerId?: string; status?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.customerId) searchParams.set('customerId', params.customerId);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/customer-portal/quote-requests${query ? `?${query}` : ''}`);
}

export async function getPortalQuoteRequest(id: string) {
  return request<any>(`/customer-portal/quote-requests/${id}`);
}

export async function updatePortalQuoteRequest(id: string, data: Partial<{
  status: string;
  quoteId: string;
  quoteValidUntil: string;
  quoteTotal: number;
  quoteCurrency: string;
  internalNotes: string;
  convertedToOrderId: string;
}>) {
  return request<any>(`/customer-portal/quote-requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Portal Message Threads
export async function getPortalMessageThreads(params?: { customerId?: string; status?: string; assignedTo?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.customerId) searchParams.set('customerId', params.customerId);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.assignedTo) searchParams.set('assignedTo', params.assignedTo);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/customer-portal/threads${query ? `?${query}` : ''}`);
}

export async function getPortalMessageThread(id: string) {
  return request<any>(`/customer-portal/threads/${id}`);
}

export async function replyToPortalThread(threadId: string, data: {
  content: string;
  messageType?: string;
  priority?: string;
  attachments?: any;
  relatedEntityType?: string;
  relatedEntityId?: string;
}) {
  return request<any>(`/customer-portal/threads/${threadId}/messages`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePortalThread(id: string, data: Partial<{
  status: string;
  priority: string;
  category: string;
  assignedTo: string;
}>) {
  return request<any>(`/customer-portal/threads/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Portal Product Catalog
export async function getPortalCatalog(params?: { category?: string; isActive?: boolean; isFeatured?: boolean; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  if (params?.isActive !== undefined) searchParams.set('isActive', String(params.isActive));
  if (params?.isFeatured !== undefined) searchParams.set('isFeatured', String(params.isFeatured));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/customer-portal/catalog${query ? `?${query}` : ''}`);
}

export async function getPortalCatalogProduct(id: string) {
  return request<any>(`/customer-portal/catalog/${id}`);
}

export async function createPortalCatalogProduct(data: {
  formulaId?: string;
  productCode: string;
  productName: string;
  description?: string;
  category?: string;
  subcategory?: string;
  fragranceFamily?: string;
  applicationTypes?: string[];
  imageUrl?: string;
  thumbnailUrl?: string;
  isActive?: boolean;
  isFeatured?: boolean;
  isNew?: boolean;
  minimumOrderQuantity?: number;
  minimumOrderUnit?: string;
  leadTimeDays?: number;
  availableSizes?: any;
  certifications?: string[];
  regulatoryStatus?: any;
  customerRestrictions?: string[];
  customerAllowlist?: string[];
  tags?: string[];
  sortOrder?: number;
  metadata?: any;
}) {
  return request<any>('/customer-portal/catalog', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePortalCatalogProduct(id: string, data: Partial<{
  formulaId: string;
  productCode: string;
  productName: string;
  description: string;
  category: string;
  subcategory: string;
  fragranceFamily: string;
  applicationTypes: string[];
  imageUrl: string;
  thumbnailUrl: string;
  isActive: boolean;
  isFeatured: boolean;
  isNew: boolean;
  minimumOrderQuantity: number;
  minimumOrderUnit: string;
  leadTimeDays: number;
  availableSizes: any;
  certifications: string[];
  regulatoryStatus: any;
  customerRestrictions: string[];
  customerAllowlist: string[];
  tags: string[];
  sortOrder: number;
  metadata: any;
}>) {
  return request<any>(`/customer-portal/catalog/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deletePortalCatalogProduct(id: string) {
  return request<{ success: boolean }>(`/customer-portal/catalog/${id}`, { method: 'DELETE' });
}

// Portal Customer Pricing
export async function getPortalCustomerPricing(params?: { customerId?: string; productId?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.customerId) searchParams.set('customerId', params.customerId);
  if (params?.productId) searchParams.set('productId', params.productId);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/customer-portal/pricing${query ? `?${query}` : ''}`);
}

export async function createPortalCustomerPricing(data: {
  customerId: string;
  productId?: string;
  formulaId?: string;
  price: number;
  currency?: string;
  priceUnit?: string;
  minimumQuantity?: number;
  discountPercentage?: number;
  effectiveDate: string;
  expirationDate?: string;
  priceListId?: string;
  notes?: string;
  isActive?: boolean;
}) {
  return request<any>('/customer-portal/pricing', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePortalCustomerPricing(id: string, data: Partial<{
  price: number;
  currency: string;
  priceUnit: string;
  minimumQuantity: number;
  discountPercentage: number;
  effectiveDate: string;
  expirationDate: string;
  notes: string;
  isActive: boolean;
}>) {
  return request<any>(`/customer-portal/pricing/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deletePortalCustomerPricing(id: string) {
  return request<{ success: boolean }>(`/customer-portal/pricing/${id}`, { method: 'DELETE' });
}

// Portal Settings
export async function getPortalSettings() {
  return request<any>('/customer-portal/settings');
}

export async function updatePortalSettings(data: Partial<{
  isEnabled: boolean;
  portalName: string;
  portalLogoUrl: string;
  primaryColor: string;
  secondaryColor: string;
  welcomeMessage: string;
  footerText: string;
  contactEmail: string;
  contactPhone: string;
  supportHours: string;
  allowedDomains: string[];
  requireApproval: boolean;
  allowSelfRegistration: boolean;
  allowSampleRequests: boolean;
  allowQuoteRequests: boolean;
  allowDocumentDownload: boolean;
  allowMessaging: boolean;
  showPricing: boolean;
  showInventoryStatus: boolean;
  defaultLanguage: string;
  sessionTimeoutMinutes: number;
  maxLoginAttempts: number;
  lockoutDurationMinutes: number;
  passwordPolicy: any;
  notificationSettings: any;
  customCss: string;
  customJs: string;
  metadata: any;
}>) {
  return request<any>('/customer-portal/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Portal Dashboard
export async function getPortalDashboard() {
  return request<{
    activeUsers: number;
    pendingSampleRequests: number;
    pendingQuoteRequests: number;
    openThreads: number;
    catalogProducts: number;
    recentActivity: any[];
  }>('/customer-portal/dashboard');
}

// ================================
// Financial Analytics (Logo ERP Data)
// ================================

export async function getFinancialSummary(params?: { year?: number }) {
  const query = new URLSearchParams();
  if (params?.year) query.set('year', params.year.toString());
  const queryString = query.toString();
  return request<{ data: any }>(`/financial-analytics/summary${queryString ? `?${queryString}` : ''}`);
}

export async function getProfitabilityAnalyses(params?: { year?: number; period?: string }) {
  const query = new URLSearchParams();
  if (params?.year) query.set('year', params.year.toString());
  if (params?.period) query.set('period', params.period);
  const queryString = query.toString();
  return request<{ data: any[] }>(`/financial-analytics/profitability${queryString ? `?${queryString}` : ''}`);
}

export async function getArAging() {
  return request<{ data: { summary: any; accounts: any[] } }>('/financial-analytics/ar-aging');
}

export async function getApAging() {
  return request<{ data: { summary: any; accounts: any[] } }>('/financial-analytics/ap-aging');
}

export async function getChartOfAccounts(params?: { search?: string; level?: number }) {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.level) query.set('level', params.level.toString());
  const queryString = query.toString();
  return request<{ data: any[] }>(`/financial-analytics/chart-of-accounts${queryString ? `?${queryString}` : ''}`);
}

export async function getJournalEntries(params?: { page?: number; pageSize?: number; year?: number; month?: number; accountCode?: string }) {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  if (params?.year) query.set('year', params.year.toString());
  if (params?.month) query.set('month', params.month.toString());
  if (params?.accountCode) query.set('accountCode', params.accountCode);
  const queryString = query.toString();
  return request<{ data: any[]; total: number; page: number; pageSize: number; totalPages: number }>(`/financial-analytics/journal-entries${queryString ? `?${queryString}` : ''}`);
}

export async function getBankSummary() {
  return request<{ data: any }>('/financial-analytics/bank-summary');
}

export async function getTopCustomers(params?: { year?: number; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.year) query.set('year', params.year.toString());
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[] }>(`/financial-analytics/top-customers${queryString ? `?${queryString}` : ''}`);
}

export async function getTopSuppliers(params?: { year?: number; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.year) query.set('year', params.year.toString());
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[] }>(`/financial-analytics/top-suppliers${queryString ? `?${queryString}` : ''}`);
}

export async function getFinancialChecks(params?: { status?: string }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  const queryString = query.toString();
  return request<{ data: any }>(`/financial-analytics/checks${queryString ? `?${queryString}` : ''}`);
}

export async function getFinancialProjects() {
  return request<{ data: any[] }>('/financial-analytics/projects');
}

// ================================
// Locations (Warehouse Management)
// ================================

export async function getStorageZones(params?: { warehouseId?: string; zoneType?: string; isActive?: boolean }) {
  const query = new URLSearchParams();
  if (params?.warehouseId) query.set('warehouseId', params.warehouseId);
  if (params?.zoneType) query.set('zoneType', params.zoneType);
  if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
  const queryString = query.toString();
  return request<any[]>(`/locations/zones${queryString ? `?${queryString}` : ''}`);
}

export async function getBinLocations(params?: { warehouseId?: string; zoneId?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.warehouseId) query.set('warehouseId', params.warehouseId);
  if (params?.zoneId) query.set('zoneId', params.zoneId);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/locations/bins${queryString ? `?${queryString}` : ''}`);
}

export async function getTransferOrders(params?: { status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/locations/transfers${queryString ? `?${queryString}` : ''}`);
}

export async function getCycleCounts(params?: { warehouseId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.warehouseId) query.set('warehouseId', params.warehouseId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/locations/cycle-counts${queryString ? `?${queryString}` : ''}`);
}

// ================================
// CRM
// ================================

export async function getCrmContacts(params?: { customerId?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/crm/contacts${queryString ? `?${queryString}` : ''}`);
}

export async function getCrmActivities(params?: { customerId?: string; activityType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.activityType) query.set('activityType', params.activityType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/crm/activities${queryString ? `?${queryString}` : ''}`);
}

export async function getCrmOpportunities(params?: { customerId?: string; status?: string; stage?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.status) query.set('status', params.status);
  if (params?.stage) query.set('stage', params.stage);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/crm/opportunities${queryString ? `?${queryString}` : ''}`);
}

// ================================
// Genealogy (Traceability)
// ================================

export async function getGenealogyTree(batchId: string, params?: { direction?: string; depth?: number }) {
  const query = new URLSearchParams();
  if (params?.direction) query.set('direction', params.direction);
  if (params?.depth) query.set('depth', params.depth.toString());
  const queryString = query.toString();
  return request<any>(`/genealogy/tree/${batchId}${queryString ? `?${queryString}` : ''}`);
}

export async function getGenealogySearch(params: { batchNumber?: string; lotNumber?: string; startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  if (params.batchNumber) query.set('batchNumber', params.batchNumber);
  if (params.lotNumber) query.set('lotNumber', params.lotNumber);
  if (params.startDate) query.set('startDate', params.startDate);
  if (params.endDate) query.set('endDate', params.endDate);
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/genealogy/search${queryString ? `?${queryString}` : ''}`);
}

export async function getAffectedBatches(batchId: string) {
  return request<{ upstream: any[]; downstream: any[] }>(`/genealogy/affected/${batchId}`);
}

// ================================
// IP Management
// ================================

export async function getTradeSecrets(params?: { category?: string; protectionLevel?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.category) query.set('category', params.category);
  if (params?.protectionLevel) query.set('protectionLevel', params.protectionLevel);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/ip-management/trade-secrets${queryString ? `?${queryString}` : ''}`);
}

export async function getPatents(params?: { status?: string; patentType?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.patentType) query.set('patentType', params.patentType);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/ip-management/patents${queryString ? `?${queryString}` : ''}`);
}

export async function getTrademarks(params?: { status?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/ip-management/trademarks${queryString ? `?${queryString}` : ''}`);
}

export async function createPatent(data: {
  title: string;
  patentNumber?: string;
  patentType: string;
  status?: string;
  filingDate?: string;
  grantDate?: string;
  expirationDate?: string;
  jurisdiction?: string;
  applicantName?: string;
  inventors?: string[];
  claims?: string;
  abstract?: string;
  relatedFormulas?: string[];
  notes?: string;
}) {
  return request<any>('/ip/patents', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createTradeSecret(data: {
  name: string;
  secretType: string;
  classification?: string;
  status?: string;
  formulaId?: string;
  valueEstimate?: number;
  currency?: string;
  protectionMeasures?: string[];
  ndaRequired?: boolean;
  description?: string;
  notes?: string;
}) {
  return request<any>('/ip/trade-secrets', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createTrademark(data: {
  name: string;
  registrationNumber?: string;
  status?: string;
  filingDate?: string;
  registrationDate?: string;
  expirationDate?: string;
  jurisdiction?: string;
  classes?: string[];
  description?: string;
  notes?: string;
}) {
  return request<any>('/ip/trademarks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getNdas(params?: { status?: string; partyName?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.partyName) query.set('partyName', params.partyName);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/ip/ndas${queryString ? `?${queryString}` : ''}`);
}

export async function createNda(data: {
  title: string;
  partyName: string;
  partyType: string;
  ndaType: string;
  status?: string;
  effectiveDate?: string;
  expirationDate?: string;
  terms?: string;
  notes?: string;
}) {
  return request<any>('/ip/ndas', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getLicensingAgreements(params?: { status?: string; licenseeType?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.licenseeType) query.set('licenseeType', params.licenseeType);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/ip/licenses${queryString ? `?${queryString}` : ''}`);
}

export async function createLicensingAgreement(data: {
  title: string;
  agreementNumber?: string;
  licenseType: string;
  licenseeType: string;
  licenseeName: string;
  status?: string;
  effectiveDate?: string;
  expirationDate?: string;
  royaltyRate?: number;
  royaltyType?: string;
  minimumRoyalty?: number;
  territory?: string[];
  grantedRights?: string[];
  relatedIp?: string[];
  terms?: string;
  notes?: string;
}) {
  return request<any>('/ip/licenses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getIpPortfolio(params?: { ipType?: string; status?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.ipType) query.set('ipType', params.ipType);
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/ip/portfolio${queryString ? `?${queryString}` : ''}`);
}

export async function getIpDisputes(params?: { status?: string; disputeType?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.disputeType) query.set('disputeType', params.disputeType);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/ip/disputes${queryString ? `?${queryString}` : ''}`);
}

export async function getIpDeadlines(params?: { status?: string; deadlineType?: string; upcomingDays?: number; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.deadlineType) query.set('deadlineType', params.deadlineType);
  if (params?.upcomingDays) query.set('upcomingDays', params.upcomingDays.toString());
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/ip/deadlines${queryString ? `?${queryString}` : ''}`);
}

export async function getIpDashboard() {
  return request<{
    patents: { total: number; pending: number; granted: number; expiringSoon: number };
    tradeSecrets: { total: number; active: number; byClassification: Record<string, number> };
    trademarks: { total: number; active: number; expiringSoon: number };
    ndas: { total: number; active: number; expiringSoon: number };
    licenses: { total: number; active: number; royaltyRevenue: number };
    disputes: { total: number; active: number };
    upcomingDeadlines: any[];
    recentActivity: any[];
  }>('/ip/dashboard');
}

// Genealogy Batch Records
export async function getGenealogyBatches(params?: { batchType?: string; entityType?: string; status?: string; qualityStatus?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.batchType) query.set('batchType', params.batchType);
  if (params?.entityType) query.set('entityType', params.entityType);
  if (params?.status) query.set('status', params.status);
  if (params?.qualityStatus) query.set('qualityStatus', params.qualityStatus);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/genealogy/batches${queryString ? `?${queryString}` : ''}`);
}

export async function getGenealogyBatch(id: string) {
  return request<any>(`/genealogy/batches/${id}`);
}

export async function getRecallEvents(params?: { status?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/genealogy/recalls${queryString ? `?${queryString}` : ''}`);
}

export async function createRecallEvent(data: {
  recallNumber?: string;
  title: string;
  recallType: string;
  severity: string;
  reason: string;
  description?: string;
  initiatedDate?: string;
  rootCause?: string;
  correctiveActions?: string;
}) {
  return request<any>('/genealogy/recalls', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ================================
// Recipe Development
// ================================

export async function getRecipeDevProjects(params?: { search?: string; type?: string; status?: string; customerId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.type) query.set('type', params.type);
  if (params?.status) query.set('status', params.status);
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/recipe-development/projects${queryString ? `?${queryString}` : ''}`);
}

export async function getRecipeDevProject(id: string) {
  return request<any>(`/recipe-development/projects/${id}`);
}

export async function createRecipeDevProject(data: {
  name: string;
  projectType: string;
  customerId?: string;
  leadDeveloperId?: string;
  description?: string;
  targetProfile?: string;
  targetCost?: number;
  deadline?: string;
  priority?: string;
}) {
  return request<any>('/recipe-development/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getTrialFormulas(params?: { projectId?: string; status?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.projectId) query.set('projectId', params.projectId);
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/recipe-development/trial-formulas${queryString ? `?${queryString}` : ''}`);
}

export async function createTrialFormula(data: {
  projectId: string;
  name: string;
  description?: string;
  targetProfile?: string;
  estimatedCost?: number;
}) {
  return request<any>('/recipe-development/trial-formulas', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getPilotBatches(params?: { trialFormulaId?: string; status?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.trialFormulaId) query.set('trialFormulaId', params.trialFormulaId);
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/recipe-development/pilot-batches${queryString ? `?${queryString}` : ''}`);
}

export async function createPilotBatch(data: {
  trialFormulaId: string;
  batchSize: number;
  unit?: string;
  productionDate?: string;
  notes?: string;
}) {
  return request<any>('/recipe-development/pilot-batches', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getRecipeDevSensoryEvaluations(params?: { trialFormulaId?: string; pilotBatchId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.trialFormulaId) query.set('trialFormulaId', params.trialFormulaId);
  if (params?.pilotBatchId) query.set('pilotBatchId', params.pilotBatchId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/recipe-development/sensory-evaluations${queryString ? `?${queryString}` : ''}`);
}

export async function getRecipeDevStabilityTests(params?: { trialFormulaId?: string; pilotBatchId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.trialFormulaId) query.set('trialFormulaId', params.trialFormulaId);
  if (params?.pilotBatchId) query.set('pilotBatchId', params.pilotBatchId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/recipe-development/stability-tests${queryString ? `?${queryString}` : ''}`);
}

export async function getRecipeDevSampleSubmissions(params?: { projectId?: string; trialFormulaId?: string; customerId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.projectId) query.set('projectId', params.projectId);
  if (params?.trialFormulaId) query.set('trialFormulaId', params.trialFormulaId);
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/recipe-development/sample-submissions${queryString ? `?${queryString}` : ''}`);
}

export async function getRecipeDevCostEstimates(params?: { trialFormulaId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.trialFormulaId) query.set('trialFormulaId', params.trialFormulaId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/recipe-development/cost-estimates${queryString ? `?${queryString}` : ''}`);
}

export async function getRecipeDevDashboard() {
  return request<{
    activeProjects: number;
    trialFormulas: number;
    pilotBatches: number;
    pendingSamples: number;
    recentActivity: any[];
    projectsByStatus: Record<string, number>;
    upcomingDeadlines: any[];
  }>('/recipe-development/dashboard');
}

// ================================
// Contract Manufacturing (CMO)
// ================================

// CMOs (Contract Manufacturers)
export async function getCmos(params?: { status?: string; qualificationStatus?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.qualificationStatus) query.set('qualificationStatus', params.qualificationStatus);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/contract-manufacturing/cmos${queryString ? `?${queryString}` : ''}`);
}

export async function getCmo(id: string) {
  return request<any>(`/contract-manufacturing/cmos/${id}`);
}

export async function createCmo(data: {
  name: string;
  code?: string;
  status?: string;
  qualificationStatus?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  capabilities?: string[];
  certifications?: string[];
  qualityRating?: number;
}) {
  return request<any>('/contract-manufacturing/cmos', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCmo(id: string, data: Partial<{
  name: string;
  status: string;
  qualificationStatus: string;
  address: string;
  city: string;
  state: string;
  country: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  capabilities: string[];
  certifications: string[];
  qualityRating: number;
}>) {
  return request<any>(`/contract-manufacturing/cmos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCmo(id: string) {
  return request<{ success: boolean }>(`/contract-manufacturing/cmos/${id}`, { method: 'DELETE' });
}

// Manufacturing Contracts
export async function getCmoContracts(params?: { cmoId?: string; status?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.cmoId) query.set('cmoId', params.cmoId);
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/contract-manufacturing/contracts${queryString ? `?${queryString}` : ''}`);
}

export async function getCmoContract(id: string) {
  return request<any>(`/contract-manufacturing/contracts/${id}`);
}

export async function createCmoContract(data: {
  cmoId: string;
  title: string;
  contractNumber?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  terms?: string;
  value?: number;
  currency?: string;
}) {
  return request<any>('/contract-manufacturing/contracts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCmoContract(id: string, data: Partial<{
  title: string;
  status: string;
  startDate: string;
  endDate: string;
  terms: string;
  value: number;
  currency: string;
}>) {
  return request<any>(`/contract-manufacturing/contracts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// CMO Work Orders
export async function getCmoWorkOrders(params?: { cmoId?: string; status?: string; priority?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.cmoId) query.set('cmoId', params.cmoId);
  if (params?.status) query.set('status', params.status);
  if (params?.priority) query.set('priority', params.priority);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/contract-manufacturing/work-orders${queryString ? `?${queryString}` : ''}`);
}

export async function getCmoWorkOrder(id: string) {
  return request<any>(`/contract-manufacturing/work-orders/${id}`);
}

export async function createCmoWorkOrder(data: {
  cmoId: string;
  workOrderNumber?: string;
  productName: string;
  productCode?: string;
  quantity: number;
  unit?: string;
  status?: string;
  priority?: string;
  requestedDeliveryDate?: string;
  specialInstructions?: string;
}) {
  return request<any>('/contract-manufacturing/work-orders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCmoWorkOrder(id: string, data: Partial<{
  productName: string;
  productCode: string;
  quantity: number;
  unit: string;
  status: string;
  priority: string;
  requestedDeliveryDate: string;
  specialInstructions: string;
}>) {
  return request<any>(`/contract-manufacturing/work-orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateCmoWorkOrderStatus(id: string, status: string) {
  return request<any>(`/contract-manufacturing/work-orders/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

// CMO Quality Agreements
export async function getCmoQualityAgreements(params?: { cmoId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.cmoId) query.set('cmoId', params.cmoId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/contract-manufacturing/quality-agreements${queryString ? `?${queryString}` : ''}`);
}

export async function createCmoQualityAgreement(data: {
  cmoId: string;
  agreementNumber?: string;
  title: string;
  status?: string;
  effectiveDate?: string;
  expirationDate?: string;
  content?: string;
}) {
  return request<any>('/contract-manufacturing/quality-agreements', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// CMO Batch Records
export async function getCmoBatchRecords(params?: { cmoId?: string; workOrderId?: string; releaseStatus?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.cmoId) query.set('cmoId', params.cmoId);
  if (params?.workOrderId) query.set('workOrderId', params.workOrderId);
  if (params?.releaseStatus) query.set('releaseStatus', params.releaseStatus);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/contract-manufacturing/batch-records${queryString ? `?${queryString}` : ''}`);
}

export async function releaseCmoBatch(id: string, releaseStatus: string) {
  return request<any>(`/contract-manufacturing/batch-records/${id}/release`, {
    method: 'PATCH',
    body: JSON.stringify({ releaseStatus }),
  });
}

// CMO Performance Metrics
export async function getCmoPerformanceMetrics(params?: { cmoId?: string; rating?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.cmoId) query.set('cmoId', params.cmoId);
  if (params?.rating) query.set('rating', params.rating);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/contract-manufacturing/performance-metrics${queryString ? `?${queryString}` : ''}`);
}

// CMO Audits
export async function getCmoAudits(params?: { cmoId?: string; auditType?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.cmoId) query.set('cmoId', params.cmoId);
  if (params?.auditType) query.set('auditType', params.auditType);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/contract-manufacturing/audits${queryString ? `?${queryString}` : ''}`);
}

export async function createCmoAudit(data: {
  cmoId: string;
  auditNumber?: string;
  auditType: string;
  status?: string;
  scheduledDate: string;
  scope?: string;
  auditors?: string[];
}) {
  return request<any>('/contract-manufacturing/audits', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// CMO Cost Tracking
export async function getCmoCosts(params?: { cmoId?: string; workOrderId?: string; costType?: string; paymentStatus?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.cmoId) query.set('cmoId', params.cmoId);
  if (params?.workOrderId) query.set('workOrderId', params.workOrderId);
  if (params?.costType) query.set('costType', params.costType);
  if (params?.paymentStatus) query.set('paymentStatus', params.paymentStatus);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/contract-manufacturing/costs${queryString ? `?${queryString}` : ''}`);
}

// CMO Dashboard
export async function getCmoDashboard() {
  return request<{
    cmosByStatus: { status: string; count: number }[];
    workOrdersByStatus: { status: string; count: number }[];
    activeContracts: number;
    pendingReleases: number;
    upcomingAudits: any[];
    costThisMonth: number;
    recentActivity: any[];
  }>('/contract-manufacturing/dashboard');
}

// ==================== Pricing Engine ====================

// Pricing Rules
export async function getPricingRules(params?: { ruleType?: string; status?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.ruleType) query.set('ruleType', params.ruleType);
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/pricing-engine/rules${queryString ? `?${queryString}` : ''}`);
}

export async function getPricingRule(id: string) {
  return request<any>(`/pricing-engine/rules/${id}`);
}

export async function createPricingRule(data: {
  ruleName: string;
  ruleType: string;
  priority?: number;
  conditions?: any;
  priceModifier?: string;
  modifierType?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  appliesToCategories?: string[];
  appliesToProducts?: string[];
  appliesToCustomers?: string[];
  minimumQuantity?: string;
  minimumOrderValue?: string;
}) {
  return request<any>('/pricing-engine/rules', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePricingRule(id: string, data: Partial<{
  ruleName: string;
  ruleType: string;
  priority: number;
  conditions: any;
  priceModifier: string;
  modifierType: string;
  effectiveFrom: string;
  effectiveTo: string;
  appliesToCategories: string[];
  appliesToProducts: string[];
  appliesToCustomers: string[];
  minimumQuantity: string;
  minimumOrderValue: string;
  status: string;
}>) {
  return request<any>(`/pricing-engine/rules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deletePricingRule(id: string) {
  return request<{ success: boolean }>(`/pricing-engine/rules/${id}`, {
    method: 'DELETE',
  });
}

// Customer Tiers
export async function getPricingCustomerTiers(params?: { limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/pricing-engine/customer-tiers${queryString ? `?${queryString}` : ''}`);
}

export async function createPricingCustomerTier(data: {
  tierName: string;
  tierCode: string;
  discountPercentage?: string;
  minimumAnnualSpend?: string;
  paymentTermsDays?: number;
  benefits?: any;
  sortOrder?: number;
}) {
  return request<any>('/pricing-engine/customer-tiers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePricingCustomerTier(id: string, data: Partial<{
  tierName: string;
  tierCode: string;
  discountPercentage: string;
  minimumAnnualSpend: string;
  paymentTermsDays: number;
  benefits: any;
  sortOrder: number;
  isActive: boolean;
}>) {
  return request<any>(`/pricing-engine/customer-tiers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function assignCustomerToPricingTier(data: { customerId: string; tierId: string; effectiveFrom?: string; effectiveTo?: string; notes?: string }) {
  return request<any>('/pricing-engine/customer-tiers/assign', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Pricing Engine Price Lists
export async function getPricingEnginePriceLists(params?: { status?: string; currency?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.currency) query.set('currency', params.currency);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/pricing-engine/price-lists${queryString ? `?${queryString}` : ''}`);
}

export async function getPricingEnginePriceList(id: string) {
  return request<any>(`/pricing-engine/price-lists/${id}`);
}

export async function createPricingEnginePriceList(data: {
  listName: string;
  listCode: string;
  currency?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  isDefault?: boolean;
  appliesToRegions?: string[];
  appliesToCustomerTiers?: string[];
}) {
  return request<any>('/pricing-engine/price-lists', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePricingEnginePriceList(id: string, data: Partial<{
  listName: string;
  listCode: string;
  currency: string;
  effectiveFrom: string;
  effectiveTo: string;
  isDefault: boolean;
  appliesToRegions: string[];
  appliesToCustomerTiers: string[];
  status: string;
}>) {
  return request<any>(`/pricing-engine/price-lists/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function addPricingEnginePriceListItem(priceListId: string, data: {
  rawMaterialId?: string;
  formulaId?: string;
  basePrice: string;
  minimumPrice?: string;
  volumeTiers?: any;
}) {
  return request<any>(`/pricing-engine/price-lists/${priceListId}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Pricing Contracts
export async function getPricingContracts(params?: { customerId?: string; status?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/pricing-engine/contracts${queryString ? `?${queryString}` : ''}`);
}

export async function getPricingContract(id: string) {
  return request<any>(`/pricing-engine/contracts/${id}`);
}

export async function createPricingContract(data: {
  contractNumber: string;
  customerId: string;
  startDate: string;
  endDate: string;
  paymentTermsDays?: number;
  minimumCommitment?: string;
  volumeCommitment?: string;
  specialTerms?: string;
}) {
  return request<any>('/pricing-engine/contracts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePricingContract(id: string, data: Partial<{
  contractNumber: string;
  startDate: string;
  endDate: string;
  status: string;
  paymentTermsDays: number;
  minimumCommitment: string;
  volumeCommitment: string;
  specialTerms: string;
}>) {
  return request<any>(`/pricing-engine/contracts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function addPricingContractItem(contractId: string, data: {
  rawMaterialId?: string;
  formulaId?: string;
  contractPrice: string;
  minimumQuantity?: string;
  maximumQuantity?: string;
  priceProtection?: boolean;
}) {
  return request<any>(`/pricing-engine/contracts/${contractId}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Price Calculation
export async function calculatePrice(data: {
  customerId?: string;
  items: Array<{
    rawMaterialId?: string;
    formulaId?: string;
    quantity: string;
  }>;
  priceListId?: string;
  contractId?: string;
  currency?: string;
  promotionCodes?: string[];
}) {
  return request<{
    items: Array<{
      rawMaterialId?: string;
      formulaId?: string;
      quantity: string;
      basePrice: string;
      adjustedPrice: string;
      appliedRules: string[];
      lineTotal: string;
    }>;
    subtotal: string;
    discounts: string;
    total: string;
    currency: string;
    appliedPromotions: string[];
  }>('/pricing-engine/calculate', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Price Changes
export async function getPriceChanges(params?: { status?: string; changeType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.changeType) query.set('changeType', params.changeType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/pricing-engine/price-changes${queryString ? `?${queryString}` : ''}`);
}

export async function createPriceChange(data: {
  changeType: string;
  rawMaterialId?: string;
  formulaId?: string;
  priceListId?: string;
  previousPrice?: string;
  newPrice: string;
  changePercentage?: string;
  reason: string;
  effectiveDate: string;
  notifyCustomers?: boolean;
  affectedCustomers?: string[];
}) {
  return request<any>('/pricing-engine/price-changes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function approvePriceChange(id: string, data: { notes?: string }) {
  return request<any>(`/pricing-engine/price-changes/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Promotions
export async function getPricingPromotions(params?: { status?: string; promotionType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.promotionType) query.set('promotionType', params.promotionType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/pricing-engine/promotions${queryString ? `?${queryString}` : ''}`);
}

export async function createPricingPromotion(data: {
  promotionName: string;
  promotionCode: string;
  promotionType: string;
  discountValue: string;
  discountType: string;
  startDate: string;
  endDate: string;
  minimumOrderValue?: string;
  maximumDiscount?: string;
  usageLimit?: number;
  usageLimitPerCustomer?: number;
  appliesToProducts?: string[];
  appliesToCategories?: string[];
  appliesToCustomers?: string[];
  stackable?: boolean;
}) {
  return request<any>('/pricing-engine/promotions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePricingPromotion(id: string, data: Partial<{
  promotionName: string;
  promotionCode: string;
  promotionType: string;
  discountValue: string;
  discountType: string;
  startDate: string;
  endDate: string;
  minimumOrderValue: string;
  maximumDiscount: string;
  usageLimit: number;
  usageLimitPerCustomer: number;
  appliesToProducts: string[];
  appliesToCategories: string[];
  appliesToCustomers: string[];
  stackable: boolean;
  status: string;
}>) {
  return request<any>(`/pricing-engine/promotions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Pricing Engine Exchange Rates
export async function getPricingEngineExchangeRates(params?: { baseCurrency?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.baseCurrency) query.set('baseCurrency', params.baseCurrency);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/pricing-engine/exchange-rates${queryString ? `?${queryString}` : ''}`);
}

export async function createPricingEngineExchangeRate(data: {
  baseCurrency: string;
  targetCurrency: string;
  rate: string;
  effectiveFrom: string;
  effectiveTo?: string;
  source?: string;
}) {
  return request<any>('/pricing-engine/exchange-rates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Pricing Simulations
export async function getPricingSimulations(params?: { status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/pricing-engine/simulations${queryString ? `?${queryString}` : ''}`);
}

export async function createPricingSimulation(data: {
  simulationName: string;
  simulationType: string;
  parameters: any;
  affectedProducts?: string[];
  affectedCustomers?: string[];
}) {
  return request<any>('/pricing-engine/simulations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function runPricingSimulation(id: string) {
  return request<any>(`/pricing-engine/simulations/${id}/run`, {
    method: 'POST',
  });
}

// Margin Targets
export async function getMarginTargets(params?: { category?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.category) query.set('category', params.category);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/pricing-engine/margin-targets${queryString ? `?${queryString}` : ''}`);
}

export async function createMarginTarget(data: {
  targetName: string;
  category?: string;
  rawMaterialId?: string;
  formulaId?: string;
  customerId?: string;
  targetMarginPercentage: string;
  minimumMarginPercentage: string;
  alertThreshold?: string;
}) {
  return request<any>('/pricing-engine/margin-targets', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Pricing Dashboard
export async function getPricingDashboard() {
  return request<{
    activeRules: number;
    activePriceLists: number;
    activeContracts: number;
    activePromotions: number;
    pendingPriceChanges: number;
    recentCalculations: any[];
    marginAlerts: any[];
    expiringContracts: any[];
    topPromotions: any[];
  }>('/pricing-engine/dashboard');
}

// ==================== Batch Genealogy & Recall ====================

// Genealogy Links
export async function getGenealogyLinks(params?: { batchId?: string; direction?: string; linkType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.batchId) query.set('batchId', params.batchId);
  if (params?.direction) query.set('direction', params.direction);
  if (params?.linkType) query.set('linkType', params.linkType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/batch-genealogy/links${queryString ? `?${queryString}` : ''}`);
}

export async function createGenealogyLink(data: {
  parentBatchId: string;
  childBatchId: string;
  linkType?: string;
  quantityUsed?: string;
  unit?: string;
  usagePercentage?: string;
  processStep?: string;
  notes?: string;
}) {
  return request<any>('/batch-genealogy/links', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteGenealogyLink(id: string) {
  return request<{ success: boolean }>(`/batch-genealogy/links/${id}`, {
    method: 'DELETE',
  });
}

// Traceability
export async function traceForward(batchId: string, maxDepth?: number) {
  const query = maxDepth ? `?maxDepth=${maxDepth}` : '';
  return request<{
    tree: any;
    totalNodes: number;
    maxDepthReached: number;
    executionTimeMs: number;
  }>(`/batch-genealogy/trace/forward/${batchId}${query}`);
}

export async function traceBackward(batchId: string, maxDepth?: number) {
  const query = maxDepth ? `?maxDepth=${maxDepth}` : '';
  return request<{
    tree: any;
    totalNodes: number;
    executionTimeMs: number;
  }>(`/batch-genealogy/trace/backward/${batchId}${query}`);
}

export async function getImpactAnalysis(batchId: string, maxDepth?: number) {
  const query = maxDepth ? `?maxDepth=${maxDepth}` : '';
  return request<{
    affectedBatches: string[];
    totalBatchesAffected: number;
    affectedCustomers: any[];
    totalCustomersAffected: number;
    executionTimeMs: number;
  }>(`/batch-genealogy/trace/impact/${batchId}${query}`);
}

// Product Recalls
export async function getProductRecalls(params?: { status?: string; recallType?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.recallType) query.set('recallType', params.recallType);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/batch-genealogy/recalls${queryString ? `?${queryString}` : ''}`);
}

export async function getProductRecall(id: string) {
  return request<any>(`/batch-genealogy/recalls/${id}`);
}

export async function createProductRecall(data: {
  title: string;
  reason: string;
  recallType?: string;
  recallClass?: string;
  healthRiskLevel?: string;
  recallCoordinator?: string;
  recallTeam?: any[];
  affectedRegions?: string[];
  description?: string;
}) {
  return request<any>('/batch-genealogy/recalls', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProductRecall(id: string, data: Partial<{
  title: string;
  reason: string;
  status: string;
  recallType: string;
  recallClass: string;
  healthRiskLevel: string;
  effectiveDate: string;
  completionDate: string;
  rootCause: string;
  correctiveActions: string;
  preventiveActions: string;
  regulatoryNotificationDate: string;
  regulatoryAgency: string;
  regulatoryReference: string;
}>) {
  return request<any>(`/batch-genealogy/recalls/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function addRecallAffectedBatch(recallId: string, data: {
  batchId: string;
  batchNumber: string;
  productName?: string;
  formulaId?: string;
  quantityAffected?: string;
  unit?: string;
  productionDate?: string;
  expiryDate?: string;
  location?: string;
  identifiedVia?: string;
}) {
  return request<any>(`/batch-genealogy/recalls/${recallId}/batches`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRecallAffectedBatch(recallId: string, batchId: string, data: Partial<{
  status: string;
  recoveryQuantity: string;
  recoveryDate: string;
  disposition: string;
  dispositionDate: string;
  dispositionNotes: string;
}>) {
  return request<any>(`/batch-genealogy/recalls/${recallId}/batches/${batchId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function addRecallAffectedCustomer(recallId: string, data: {
  customerId?: string;
  customerName: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  batchesShipped?: any[];
  totalQuantity?: string;
  unit?: string;
  shipDates?: string[];
}) {
  return request<any>(`/batch-genealogy/recalls/${recallId}/customers`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function sendRecallCustomerNotification(recallId: string, customerId: string, data: {
  recipientName: string;
  subject: string;
  content: string;
  templateUsed?: string;
  attachments?: any[];
  sentVia?: string;
  responseRequired?: boolean;
  responseDeadline?: string;
}) {
  return request<any>(`/batch-genealogy/recalls/${recallId}/customers/${customerId}/notify`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Recall Simulations
export async function getRecallSimulations(params?: { status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/batch-genealogy/simulations${queryString ? `?${queryString}` : ''}`);
}

export async function createRecallSimulation(data: {
  simulationName: string;
  scenario: string;
  simulationType?: string;
  simulatedBatchId?: string;
  simulatedIngredientId?: string;
  targetCompletionHours?: number;
  participants?: any[];
}) {
  return request<any>('/batch-genealogy/simulations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function startRecallSimulation(id: string) {
  return request<any>(`/batch-genealogy/simulations/${id}/start`, {
    method: 'POST',
  });
}

export async function completeRecallSimulation(id: string, data: {
  traceResults?: any;
  batchesIdentified?: number;
  customersIdentified?: number;
  notificationTimeMinutes?: number;
  recoveryPercentage?: string;
  findings?: string;
  gapsIdentified?: string[];
  recommendations?: string[];
  score?: number;
  passed?: boolean;
  nextSimulationDate?: string;
}) {
  return request<any>(`/batch-genealogy/simulations/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Supplier Traceability
export async function getSupplierTraceability(params?: { supplierId?: string; lotId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.lotId) query.set('lotId', params.lotId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/batch-genealogy/supplier-traceability${queryString ? `?${queryString}` : ''}`);
}

export async function createSupplierTraceability(data: {
  supplierId: string;
  lotId: string;
  supplierLotNumber?: string;
  supplierBatchNumber?: string;
  originCountry?: string;
  originRegion?: string;
  farmOrFacility?: string;
  harvestDate?: string;
  processingDate?: string;
  certifications?: any[];
  chainOfCustody?: any[];
  gpsCoordinates?: string;
  sustainabilityData?: any;
  allergenInfo?: any;
  testingResults?: any[];
  documents?: any[];
  blockchainHash?: string;
}) {
  return request<any>('/batch-genealogy/supplier-traceability', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function verifySupplierTraceability(id: string) {
  return request<any>(`/batch-genealogy/supplier-traceability/${id}/verify`, {
    method: 'POST',
  });
}

// Customer Distribution
export async function getCustomerDistribution(params?: { batchId?: string; customerId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.batchId) query.set('batchId', params.batchId);
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/batch-genealogy/distribution${queryString ? `?${queryString}` : ''}`);
}

export async function createCustomerDistribution(data: {
  batchId: string;
  batchNumber: string;
  customerId: string;
  customerName: string;
  orderId?: string;
  orderNumber?: string;
  shipmentId?: string;
  shipmentDate?: string;
  quantityShipped: string;
  unit?: string;
  destinationAddress?: string;
  destinationCountry?: string;
  deliveryDate?: string;
  deliveryConfirmed?: boolean;
  endUse?: string;
  subCustomers?: any[];
  trackingNumber?: string;
  carrier?: string;
}) {
  return request<any>('/batch-genealogy/distribution', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Traceability Alerts
export async function getTraceabilityAlerts(params?: { isActive?: boolean; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.isActive !== undefined) query.set('isActive', params.isActive.toString());
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/batch-genealogy/alerts${queryString ? `?${queryString}` : ''}`);
}

export async function createTraceabilityAlert(data: {
  alertName: string;
  alertType: string;
  triggerConditions: any;
  traceDirection?: string;
  traceDepth?: number;
  autoQuarantine?: boolean;
  notifyCustomers?: boolean;
  notificationTemplate?: string;
  recipients?: any[];
}) {
  return request<any>('/batch-genealogy/alerts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTraceabilityAlert(id: string, data: Partial<{
  alertName: string;
  alertType: string;
  triggerConditions: any;
  traceDirection: string;
  traceDepth: number;
  autoQuarantine: boolean;
  notifyCustomers: boolean;
  notificationTemplate: string;
  recipients: any[];
  isActive: boolean;
}>) {
  return request<any>(`/batch-genealogy/alerts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Batch Genealogy Dashboard
export async function getBatchGenealogyDashboard() {
  return request<{
    activeRecalls: any[];
    recallsByStatus: { status: string; count: number }[];
    recentSimulations: any[];
    totalGenealogyLinks: number;
    totalDistributionRecords: number;
  }>('/batch-genealogy/dashboard');
}

// ==================== Cost Optimization ====================

// Optimization Projects
export async function getCostOptimizationProjects(params?: { status?: string; priority?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.priority) query.set('priority', params.priority);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/cost-optimization/projects${queryString ? `?${queryString}` : ''}`);
}

export async function getCostOptimizationProject(id: string) {
  return request<any>(`/cost-optimization/projects/${id}`);
}

export async function createCostOptimizationProject(data: {
  projectName: string;
  projectCode?: string;
  formulaId?: string;
  formulaName?: string;
  currentCost?: string;
  targetCost?: string;
  targetSavingsPercentage?: string;
  priority?: string;
  optimizationType?: string;
  constraints?: any;
  startDate?: string;
  targetCompletionDate?: string;
  assignedTo?: string;
  teamMembers?: any[];
  notes?: string;
}) {
  return request<any>('/cost-optimization/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCostOptimizationProject(id: string, data: Partial<{
  projectName: string;
  status: string;
  priority: string;
  targetCost: string;
  targetSavingsPercentage: string;
  targetCompletionDate: string;
  actualCompletionDate: string;
  achievedSavings: string;
  achievedSavingsPercentage: string;
  notes: string;
}>) {
  return request<any>(`/cost-optimization/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCostOptimizationProject(id: string) {
  return request<{ success: boolean }>(`/cost-optimization/projects/${id}`, {
    method: 'DELETE',
  });
}

// Alternative Ingredients
export async function getAlternativeIngredients(params?: { materialId?: string; testingStatus?: string; isActive?: boolean; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.materialId) query.set('materialId', params.materialId);
  if (params?.testingStatus) query.set('testingStatus', params.testingStatus);
  if (params?.isActive !== undefined) query.set('isActive', params.isActive.toString());
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/cost-optimization/alternatives${queryString ? `?${queryString}` : ''}`);
}

export async function createAlternativeIngredient(data: {
  originalMaterialId: string;
  originalMaterialName: string;
  alternativeMaterialId: string;
  alternativeMaterialName: string;
  substitutionRatio?: string;
  costDifferencePercentage?: string;
  qualityScore?: number;
  sensoryMatchScore?: number;
  functionalEquivalence?: string;
  regulatoryStatus?: string;
  applications?: any[];
  restrictions?: any[];
  notes?: string;
}) {
  return request<any>('/cost-optimization/alternatives', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAlternativeIngredient(id: string, data: Partial<{
  substitutionRatio: string;
  costDifferencePercentage: string;
  qualityScore: number;
  sensoryMatchScore: number;
  testingStatus: string;
  testResults: any[];
  isActive: boolean;
}>) {
  return request<any>(`/cost-optimization/alternatives/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Cost Scenarios
export async function getCostScenarios(params?: { projectId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.projectId) query.set('projectId', params.projectId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/cost-optimization/scenarios${queryString ? `?${queryString}` : ''}`);
}

export async function getCostScenario(id: string) {
  return request<any>(`/cost-optimization/scenarios/${id}`);
}

export async function createCostScenario(data: {
  projectId?: string;
  scenarioName: string;
  scenarioType?: string;
  baseFormulaId?: string;
  baseCost?: string;
  scenarioCost?: string;
  changes?: any[];
  impactAssessment?: any;
  riskLevel?: string;
  ingredientChanges?: any[];
}) {
  return request<any>('/cost-optimization/scenarios', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCostScenario(id: string, data: Partial<{
  scenarioName: string;
  scenarioCost: string;
  costDifference: string;
  savingsPercentage: string;
  changes: any[];
  impactAssessment: any;
  riskLevel: string;
  feasibilityScore: number;
  recommendation: string;
  status: string;
}>) {
  return request<any>(`/cost-optimization/scenarios/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function approveCostScenario(id: string, data?: { implementationNotes?: string }) {
  return request<any>(`/cost-optimization/scenarios/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

// Cost Benchmarks
export async function getCostBenchmarks(params?: { benchmarkType?: string; category?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.benchmarkType) query.set('benchmarkType', params.benchmarkType);
  if (params?.category) query.set('category', params.category);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/cost-optimization/benchmarks${queryString ? `?${queryString}` : ''}`);
}

export async function createCostBenchmark(data: {
  benchmarkName: string;
  benchmarkType: string;
  category?: string;
  productType?: string;
  region?: string;
  costPerKgLow?: string;
  costPerKgMid?: string;
  costPerKgHigh?: string;
  currency?: string;
  effectiveDate?: string;
  source?: string;
  confidenceLevel?: string;
  notes?: string;
}) {
  return request<any>('/cost-optimization/benchmarks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Supplier Cost Comparisons
export async function getSupplierCostComparisons(params?: { materialId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.materialId) query.set('materialId', params.materialId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/cost-optimization/supplier-comparisons${queryString ? `?${queryString}` : ''}`);
}

export async function createSupplierCostComparison(data: {
  materialId: string;
  materialName: string;
  suppliers: Array<{ supplierId: string; supplierName: string; unitPrice: string; moq?: string; leadTime?: number; quality?: number }>;
  currentSupplierId?: string;
  currentPrice?: string;
  recommendation?: string;
  notes?: string;
}) {
  return request<any>('/cost-optimization/supplier-comparisons', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Cost Drivers
export async function getCostDrivers(params?: { formulaId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/cost-optimization/cost-drivers${queryString ? `?${queryString}` : ''}`);
}

export async function createCostDriverAnalysis(data: {
  analysisName: string;
  formulaId?: string;
  formulaName?: string;
  totalCost?: string;
  costBreakdown?: any;
  topCostDrivers?: any[];
  varianceFromTarget?: string;
  varianceReasons?: string[];
  improvementOpportunities?: any[];
  notes?: string;
}) {
  return request<any>('/cost-optimization/cost-drivers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Optimization Recommendations
export async function getOptimizationRecommendations(params?: { projectId?: string; status?: string; recommendationType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.projectId) query.set('projectId', params.projectId);
  if (params?.status) query.set('status', params.status);
  if (params?.recommendationType) query.set('recommendationType', params.recommendationType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/cost-optimization/recommendations${queryString ? `?${queryString}` : ''}`);
}

export async function createOptimizationRecommendation(data: {
  projectId?: string;
  formulaId?: string;
  recommendationType: string;
  title: string;
  description?: string;
  currentState?: string;
  proposedChange?: string;
  estimatedSavings?: string;
  estimatedSavingsPercentage?: string;
  implementationEffort?: string;
  implementationTimeDays?: number;
  riskLevel?: string;
  qualityImpact?: string;
  regulatoryImpact?: string;
  customerApprovalRequired?: boolean;
}) {
  return request<any>('/cost-optimization/recommendations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOptimizationRecommendation(id: string, data: Partial<{
  title: string;
  description: string;
  status: string;
  actualSavings: string;
  verificationNotes: string;
}>) {
  return request<any>(`/cost-optimization/recommendations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Material Price Forecasts
export async function getMaterialPriceForecasts(params?: { materialId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.materialId) query.set('materialId', params.materialId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/cost-optimization/price-forecasts${queryString ? `?${queryString}` : ''}`);
}

export async function createMaterialPriceForecast(data: {
  materialId: string;
  materialName: string;
  forecastPeriod: string;
  currentPrice: string;
  forecastedPrice: string;
  confidenceLevel?: string;
  forecastMethod?: string;
  factors?: any[];
  dataSources?: any[];
  notes?: string;
}) {
  return request<any>('/cost-optimization/price-forecasts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Cost Savings Tracker
export async function getCostSavings(params?: { savingsType?: string; verificationStatus?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.savingsType) query.set('savingsType', params.savingsType);
  if (params?.verificationStatus) query.set('verificationStatus', params.verificationStatus);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/cost-optimization/savings${queryString ? `?${queryString}` : ''}`);
}

export async function createCostSavingsRecord(data: {
  savingsType: string;
  referenceId?: string;
  referenceName?: string;
  category?: string;
  description: string;
  periodStart: string;
  periodEnd?: string;
  baselineCost: string;
  actualCost?: string;
  annualizedSavings?: string;
  evidence?: any[];
  notes?: string;
}) {
  return request<any>('/cost-optimization/savings', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function verifyCostSavings(id: string) {
  return request<any>(`/cost-optimization/savings/${id}/verify`, {
    method: 'POST',
  });
}

// Cost Optimization Dashboard
export async function getCostOptimizationDashboard() {
  return request<{
    projectsByStatus: { status: string; count: number }[];
    activeProjects: any[];
    pendingRecommendations: any[];
    totalVerifiedSavings: string;
    recentForecasts: any[];
  }>('/cost-optimization/dashboard');
}

// ==================== Report Templates ====================

export async function getReportTemplates(params?: { category?: string; reportType?: string; isActive?: boolean }) {
  const query = new URLSearchParams();
  if (params?.category) query.set('category', params.category);
  if (params?.reportType) query.set('reportType', params.reportType);
  if (params?.isActive !== undefined) query.set('isActive', params.isActive.toString());
  const queryString = query.toString();
  return request<{ data: any[] }>(`/report-templates${queryString ? `?${queryString}` : ''}`);
}

export async function getReportTemplate(id: string) {
  return request<any>(`/report-templates/${id}`);
}

export async function createReportTemplate(data: {
  code: string;
  name: string;
  description?: string;
  category: string;
  reportType: string;
  dataSource: string;
  query?: string;
  columns?: any[];
  groupBy?: string[];
  sortBy?: any[];
  filters?: any[];
  defaultFilters?: any;
  chartConfig?: any;
  format?: string;
  exportFormats?: string[];
}) {
  return request<any>('/report-templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateReportTemplate(id: string, data: Partial<{
  name: string;
  description: string;
  category: string;
  reportType: string;
  dataSource: string;
  query: string;
  columns: any[];
  groupBy: string[];
  sortBy: any[];
  filters: any[];
  defaultFilters: any;
  chartConfig: any;
  format: string;
  exportFormats: string[];
  isActive: boolean;
}>) {
  return request<any>(`/report-templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteReportTemplate(id: string) {
  return request<{ success: boolean }>(`/report-templates/${id}`, { method: 'DELETE' });
}

export async function executeReportTemplate(id: string, params?: { filters?: any; format?: string }) {
  return request<{ data: any[]; columns: any[]; summary?: any }>(`/report-templates/${id}/execute`, {
    method: 'POST',
    body: JSON.stringify(params || {}),
  });
}

export async function getReportExecutions(templateId: string, params?: { limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[] }>(`/report-templates/${templateId}/executions${queryString ? `?${queryString}` : ''}`);
}

export async function getScheduledReports(params?: { isActive?: boolean }) {
  const query = new URLSearchParams();
  if (params?.isActive !== undefined) query.set('isActive', params.isActive.toString());
  const queryString = query.toString();
  return request<{ data: any[] }>(`/report-templates/scheduled${queryString ? `?${queryString}` : ''}`);
}

export async function createScheduledReport(data: {
  templateId: string;
  name: string;
  schedule: string;
  scheduleConfig?: any;
  filters?: any;
  format?: string;
  recipients?: string[];
  emailSubject?: string;
  emailBody?: string;
}) {
  return request<any>('/report-templates/scheduled', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateScheduledReport(id: string, data: Partial<{
  name: string;
  schedule: string;
  scheduleConfig: any;
  filters: any;
  format: string;
  recipients: string[];
  emailSubject: string;
  emailBody: string;
  isActive: boolean;
}>) {
  return request<any>(`/report-templates/scheduled/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteScheduledReport(id: string) {
  return request<{ success: boolean }>(`/report-templates/scheduled/${id}`, { method: 'DELETE' });
}

export async function toggleScheduledReport(id: string) {
  return request<any>(`/report-templates/scheduled/${id}/toggle`, { method: 'POST' });
}

export async function runScheduledReportNow(id: string) {
  return request<any>(`/report-templates/scheduled/${id}/run-now`, { method: 'POST' });
}

// ==================== Validation ====================

export async function getValidationProtocols(params?: { status?: string; type?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.type) query.set('type', params.type);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/validation/protocols${queryString ? `?${queryString}` : ''}`);
}

export async function getValidationProtocol(id: string) {
  return request<any>(`/validation/protocols/${id}`);
}

export async function createValidationProtocol(data: {
  title: string;
  protocolNumber?: string;
  validationType: string;
  scope?: string;
  objectives?: string;
  acceptanceCriteria?: any[];
  testProcedures?: any[];
  equipmentRequired?: string[];
}) {
  return request<any>('/validation/protocols', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getValidationExecutions(params?: { protocolId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.protocolId) query.set('protocolId', params.protocolId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/validation/executions${queryString ? `?${queryString}` : ''}`);
}

export async function createValidationExecution(data: {
  protocolId: string;
  executionNumber?: string;
  scheduledDate?: string;
  equipmentId?: string;
  processId?: string;
  batchId?: string;
}) {
  return request<any>('/validation/executions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getValidationDeviations(params?: { executionId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.executionId) query.set('executionId', params.executionId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/validation/deviations${queryString ? `?${queryString}` : ''}`);
}

export async function getValidationDashboard() {
  return request<{
    activeProtocols: number;
    pendingExecutions: number;
    completedThisMonth: number;
    openDeviations: number;
    upcomingValidations: any[];
    recentActivity: any[];
    statusBreakdown: Record<string, number>;
  }>('/validation/dashboard');
}

// ==================== Product Briefs ====================

export async function getProductBriefs(params?: { status?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/product-briefs${queryString ? `?${queryString}` : ''}`);
}

// ==================== Yield Analysis ====================

export async function getYieldAnalysis(params?: { period?: string; formulaId?: string }) {
  const query = new URLSearchParams();
  if (params?.period) query.set('period', params.period);
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  const queryString = query.toString();
  return request<any>(`/analytics/yield${queryString ? `?${queryString}` : ''}`);
}

// ==================== Formula Version Control ====================

// Formula Versions
export async function getFormulaVersions(params?: { formulaId?: string; status?: string; branch?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  if (params?.status) query.set('status', params.status);
  if (params?.branch) query.set('branch', params.branch);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/formula-versions${queryString ? `?${queryString}` : ''}`);
}

export async function getFormulaVersion(id: string) {
  return request<any>(`/formula-versions/${id}`);
}

export async function createFormulaVersionRecord(data: {
  formulaId: string;
  versionType?: string;
  branchName?: string;
  parentVersionId?: string;
  changeSummary?: string;
  changeReason?: string;
  changeCategory?: string;
  breakingChanges?: boolean;
  testingRequired?: boolean;
}) {
  return request<any>('/formula-versions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFormulaVersionRecord(id: string, data: Partial<{
  status: string;
  changeSummary: string;
  changeReason: string;
  testResults: any[];
  regulatoryImpact: string;
}>) {
  return request<any>(`/formula-versions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function approveFormulaVersion(id: string, data?: { notes?: string }) {
  return request<any>(`/formula-versions/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

export async function rejectFormulaVersion(id: string, data: { reason: string }) {
  return request<any>(`/formula-versions/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function setCurrentVersion(id: string) {
  return request<any>(`/formula-versions/${id}/set-current`, {
    method: 'POST',
  });
}

// Formula Branches
export async function getFormulaBranches(params?: { formulaId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/formula-versions/branches${queryString ? `?${queryString}` : ''}`);
}

export async function getFormulaBranch(id: string) {
  return request<any>(`/formula-versions/branches/${id}`);
}

export async function createFormulaBranch(data: {
  formulaId: string;
  branchName: string;
  branchType?: string;
  description?: string;
  baseVersionId?: string;
}) {
  return request<any>('/formula-versions/branches', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFormulaBranch(id: string, data: Partial<{
  description: string;
  status: string;
  isProtected: boolean;
}>) {
  return request<any>(`/formula-versions/branches/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Formula Merge Requests
export async function getFormulaMergeRequests(params?: { formulaId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/formula-versions/merge-requests${queryString ? `?${queryString}` : ''}`);
}

export async function getFormulaMergeRequest(id: string) {
  return request<any>(`/formula-versions/merge-requests/${id}`);
}

export async function createFormulaMergeRequest(data: {
  formulaId: string;
  title: string;
  description?: string;
  sourceBranch: string;
  sourceVersionId: string;
  targetBranch?: string;
  reviewers?: string[];
  requiredApprovals?: number;
}) {
  return request<any>('/formula-versions/merge-requests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFormulaMergeRequest(id: string, data: Partial<{
  title: string;
  description: string;
  reviewers: string[];
  requiredApprovals: number;
}>) {
  return request<any>(`/formula-versions/merge-requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function approveMergeRequest(id: string, data?: { comments?: string }) {
  return request<any>(`/formula-versions/merge-requests/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

export async function mergeMergeRequest(id: string, data?: { squash?: boolean; deleteSourceBranch?: boolean }) {
  return request<any>(`/formula-versions/merge-requests/${id}/merge`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

export async function closeMergeRequest(id: string) {
  return request<any>(`/formula-versions/merge-requests/${id}/close`, {
    method: 'POST',
  });
}

// Formula Version Reviews
export async function getFormulaVersionReviews(params?: { versionId?: string; mergeRequestId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.versionId) query.set('versionId', params.versionId);
  if (params?.mergeRequestId) query.set('mergeRequestId', params.mergeRequestId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/formula-versions/reviews${queryString ? `?${queryString}` : ''}`);
}

export async function createFormulaVersionReview(data: {
  versionId: string;
  mergeRequestId?: string;
  reviewType?: string;
  checklist?: any[];
  reviewAreas?: string[];
}) {
  return request<any>('/formula-versions/reviews', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function submitFormulaReview(id: string, data: {
  status: string;
  comments?: string;
}) {
  return request<any>(`/formula-versions/reviews/${id}/submit`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Formula Version Comments
export async function getFormulaVersionComments(params?: { versionId?: string; mergeRequestId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.versionId) query.set('versionId', params.versionId);
  if (params?.mergeRequestId) query.set('mergeRequestId', params.mergeRequestId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/formula-versions/comments${queryString ? `?${queryString}` : ''}`);
}

export async function createFormulaVersionComment(data: {
  versionId: string;
  mergeRequestId?: string;
  parentCommentId?: string;
  commentType?: string;
  content: string;
  lineReference?: any;
}) {
  return request<any>('/formula-versions/comments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function resolveFormulaVersionComment(id: string) {
  return request<any>(`/formula-versions/comments/${id}/resolve`, {
    method: 'POST',
  });
}

// Formula Comparisons
export async function getFormulaComparisons(params?: { formulaId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/formula-versions/comparisons${queryString ? `?${queryString}` : ''}`);
}

export async function createFormulaComparison(data: {
  formulaId: string;
  versionAId: string;
  versionBId: string;
  comparisonName?: string;
  comparisonType?: string;
}) {
  return request<any>('/formula-versions/comparisons', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getFormulaComparison(id: string) {
  return request<any>(`/formula-versions/comparisons/${id}`);
}

// Formula Collaboration Sessions
export async function getFormulaCollaborationSessions(params?: { formulaId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/formula-versions/collaboration${queryString ? `?${queryString}` : ''}`);
}

export async function createFormulaCollaborationSession(data: {
  formulaId: string;
  versionId?: string;
  sessionName?: string;
  sessionType?: string;
  allowEditing?: boolean;
  chatEnabled?: boolean;
}) {
  return request<any>('/formula-versions/collaboration', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function joinFormulaCollaborationSession(id: string) {
  return request<any>(`/formula-versions/collaboration/${id}/join`, {
    method: 'POST',
  });
}

export async function leaveFormulaCollaborationSession(id: string) {
  return request<any>(`/formula-versions/collaboration/${id}/leave`, {
    method: 'POST',
  });
}

export async function endFormulaCollaborationSession(id: string) {
  return request<any>(`/formula-versions/collaboration/${id}/end`, {
    method: 'POST',
  });
}

export async function sendCollaborationMessage(sessionId: string, data: {
  messageType?: string;
  content: string;
  referenceData?: any;
}) {
  return request<any>(`/formula-versions/collaboration/${sessionId}/messages`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getCollaborationMessages(sessionId: string, params?: { limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/formula-versions/collaboration/${sessionId}/messages${queryString ? `?${queryString}` : ''}`);
}

// Formula Approval Workflows
export async function getFormulaApprovalWorkflows(params?: { isActive?: boolean; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.isActive !== undefined) query.set('isActive', params.isActive.toString());
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/formula-versions/workflows${queryString ? `?${queryString}` : ''}`);
}

export async function getFormulaApprovalWorkflow(id: string) {
  return request<any>(`/formula-versions/workflows/${id}`);
}

export async function createFormulaApprovalWorkflow(data: {
  workflowName: string;
  description?: string;
  workflowType?: string;
  steps: any[];
  triggerConditions?: any;
  escalationRules?: any;
  slaHours?: number;
}) {
  return request<any>('/formula-versions/workflows', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFormulaApprovalWorkflow(id: string, data: Partial<{
  workflowName: string;
  description: string;
  steps: any[];
  isActive: boolean;
  isDefault: boolean;
}>) {
  return request<any>(`/formula-versions/workflows/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Formula Approval Instances
export async function startFormulaApproval(data: {
  workflowId: string;
  versionId: string;
  mergeRequestId?: string;
}) {
  return request<any>('/formula-versions/approvals', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getFormulaApprovalInstance(id: string) {
  return request<any>(`/formula-versions/approvals/${id}`);
}

export async function processApprovalStep(instanceId: string, data: {
  decision: string;
  notes?: string;
}) {
  return request<any>(`/formula-versions/approvals/${instanceId}/process`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function cancelFormulaApproval(instanceId: string, data?: { reason?: string }) {
  return request<any>(`/formula-versions/approvals/${instanceId}/cancel`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

// Formula Version Tags
export async function addFormulaVersionTag(versionId: string, data: {
  tagName: string;
  tagType?: string;
  description?: string;
  color?: string;
}) {
  return request<any>(`/formula-versions/${versionId}/tags`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function removeFormulaVersionTag(versionId: string, tagId: string) {
  return request<{ success: boolean }>(`/formula-versions/${versionId}/tags/${tagId}`, {
    method: 'DELETE',
  });
}

// Formula Version Attachments
export async function getFormulaVersionAttachments(versionId: string) {
  return request<{ data: any[] }>(`/formula-versions/${versionId}/attachments`);
}

export async function addFormulaVersionAttachment(versionId: string, data: {
  fileName: string;
  fileType?: string;
  fileSize?: number;
  filePath?: string;
  fileUrl?: string;
  attachmentType?: string;
  description?: string;
}) {
  return request<any>(`/formula-versions/${versionId}/attachments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteFormulaVersionAttachment(versionId: string, attachmentId: string) {
  return request<{ success: boolean }>(`/formula-versions/${versionId}/attachments/${attachmentId}`, {
    method: 'DELETE',
  });
}

// Formula Change History
export async function getFormulaChangeHistory(params?: { formulaId?: string; versionId?: string; changeType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  if (params?.versionId) query.set('versionId', params.versionId);
  if (params?.changeType) query.set('changeType', params.changeType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/formula-versions/history${queryString ? `?${queryString}` : ''}`);
}

// Formula Version Dashboard
export async function getFormulaVersionDashboard(params?: { formulaId?: string }) {
  const query = new URLSearchParams();
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  const queryString = query.toString();
  return request<{
    totalVersions: number;
    activeBranches: number;
    openMergeRequests: number;
    pendingReviews: number;
    pendingApprovals: number;
    activeCollaborations: number;
    recentVersions: any[];
    recentMergeRequests: any[];
    recentChanges: any[];
  }>(`/formula-versions/dashboard${queryString ? `?${queryString}` : ''}`);
}
